import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { BarChart3, ArrowRight, Users } from "lucide-react";
import api from "@/lib/api";
import { PageHero, Loading, Empty } from "@/components/common";
import { Notice } from "@/components/Notices";

function ResultBar({ label, count, total }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div data-testid="result-bar">
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-slate-700">{label}</span>
        <span className="font-semibold text-slate-900">{pct}% · {count.toLocaleString("es-PR")}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ResultCard({ poll }) {
  const total = poll.total_votes || 0;
  const results = poll.results || {};
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid={`result-card-${poll.id}`}>
      <div className="flex items-start justify-between gap-3 mb-1">
        <span className="text-xs font-semibold text-cyan-600 uppercase tracking-wide">{poll.poll_type}</span>
        <span className="text-[11px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">Nivel 1 · Consulta abierta</span>
      </div>
      <h3 className="font-heading font-bold text-lg text-slate-900 mb-4">{poll.question}</h3>
      <div className="space-y-3">
        {poll.options.map((o) => <ResultBar key={o.id} label={o.label} count={results[o.id] || 0} total={total} />)}
      </div>
      <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100">
        <p className="inline-flex items-center gap-1.5 text-sm text-slate-500"><Users className="w-4 h-4" /> {total.toLocaleString("es-PR")} respuestas válidas</p>
        <Link to={`/relampago/${poll.id}`} className="text-sm font-semibold text-cyan-600 hover:text-cyan-700 inline-flex items-center gap-1" data-testid={`result-detail-${poll.id}`}>
          Ver detalle <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <p className="text-xs text-slate-400 mt-3 leading-relaxed">Cierre: {new Date(poll.closes_at).toLocaleString("es-PR")}</p>
    </div>
  );
}

export default function Resultados() {
  const [polls, setPolls] = useState(null);

  const load = useCallback(() => {
    api.get("/flash", { params: { scope: "closed", country: "puerto-rico" } })
      .then((r) => setPolls(r.data))
      .catch(() => setPolls([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="resultados-page">
      <PageHero
        label="Resultados"
        title="Lo que respondió la ciudadanía"
        subtitle="Resultados de consultas cerradas. Cada uno indica su periodo, número de respuestas válidas, exclusiones y nivel metodológico. Nada aquí es una elección oficial."
      />
      <div className="max-w-5xl mx-auto px-6 py-12 space-y-8">
        <Notice variant="resultados" tone="info" />
        {polls === null ? <Loading /> : polls.length === 0 ? (
          <Empty label="Todavía no hay resultados publicados. Vuelve cuando cierre la próxima consulta." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {polls.map((p) => <ResultCard key={p.id} poll={p} />)}
          </div>
        )}
        <div className="bg-slate-100 rounded-lg p-5 flex items-start gap-3">
          <BarChart3 className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600 leading-relaxed">¿Quieres entender cómo se producen estos números? Consulta nuestra <Link to="/metodologia" className="text-cyan-600 font-semibold hover:underline">metodología</Link>.</p>
        </div>
      </div>
    </div>
  );
}
